import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from '../utils/Seo';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase-config';

import Hero from './Hero';
import Portfolio from './Portfolio';
import Story from './Story';
import Gallery from './Gallery';
import FeaturedVideos from './FeaturedVideos';
import FeaturedPackages from './FeaturedPackages';
import ContactForm from './ContactForm';
import Footer from './Footer';
import Loader from './Loader';

function LandingPage() {
  const { i18n } = useTranslation();
  
  const [settings, setSettings] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [featuredPackages, setFeaturedPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);

  useEffect(() => {
    const fetchLandingData = async () => {
      try {
        const [settingsResult, packagesResult] = await Promise.allSettled([
          getDocs(collection(db, "settings")),
          getDocs(query(collection(db, "packages"), where("prikaziNaNaslovnoj", "==", true)))
        ]);

        if (settingsResult.status === 'fulfilled' && !settingsResult.value.empty) {
          const settingsData = settingsResult.value.docs[0].data();
          setSettings(settingsData);
        }

        if (packagesResult.status === 'fulfilled') {
          const packages = packagesResult.value.docs.map(doc => ({
            firestoreId: doc.id,
            ...doc.data()
          }));
          setFeaturedPackages(packages);
        }
        
        // Ensure loader stays for at least 800ms for smooth transition
        const elapsed = Date.now() - startTime;
        const delay = Math.max(0, 800 - elapsed);
        
        setTimeout(() => {
          setLoading(false);
        }, delay);
      } catch (err) {
        console.error("Unexpected error in fetchData:", err);
        setLoading(false);
      }
    };
    const startTime = Date.now();
    fetchLandingData();

    // Handle hash scroll for navigation from other pages
    const checkHash = () => {
      const hash = window.location.hash;
      if (hash) {
        // Wait a bit for components to mount
        setTimeout(() => {
          const id = hash.replace('#', '');
          const element = document.getElementById(id);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 500);
      }
    };

    checkHash();
    // Also listen for hash changes if on the same page
    window.addEventListener('hashchange', checkHash);
    return () => window.removeEventListener('hashchange', checkHash);
  }, []);

  if (loading) {
    return <Loader label={i18n.language === 'bs' ? 'Učitavanje ponude...' : 'Loading offer...'} />;
  }

  return (
    <>
      <Helmet>
        <title>Nejira Zukić Weddings</title>
        <meta name="description" content="Vaša personalizovana ponuda za vjenčanje - Nejira Zukić Weddings. Elegantan i luksuzan pristup vašem najvažnijem danu." />
        <meta name="keywords" content="Nejira Zukić, fotograf vjenčanja, videograf vjenčanja, wedding photographer Sarajevo, wedding videographer Bosnia, luksuzna vjenčanja, profesionalna fotografija, vjenčani video" />

        {/* Open Graph */}
        <meta property="og:title" content="Nejira Zukić Weddings" />
        <meta property="og:image" content="https://nejirazukic-weddings.web.app/img/NZ%20logo%20crni.png" />
        <meta property="og:url" content="https://nejirazukic-weddings.web.app/" />

        {/* Twitter */}
        <meta name="twitter:title" content="Nejira Zukić Weddings" />
        <meta name="twitter:image" content="https://nejirazukic-weddings.web.app/img/NZ%20logo%20crni.png" />

        {/* Additional SEO */}
        <link rel="canonical" href="https://nejirazukic-weddings.web.app/" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
      </Helmet>

      <div className="w-full font-sans bg-[#fcfaf8]">
      {/* HERO SECTION */}
      <div id="home">
        <Hero settings={settings} featuredPackages={featuredPackages} isMenuOpen={isMenuOpen} toggleMenu={() => setIsMenuOpen(prev => !prev)} />
      </div>

      {/* PORTFOLIO SECTION */}
      <div id="portfolio">
        <Portfolio settings={settings} />
      </div>

      {/* STORY SECTION */}
      <div id="story">
        <Story settings={settings} />
      </div>

      {/* GALLERY SECTION */}
      <div id="gallery">
        <Gallery settings={settings} />
      </div>

      {/* FEATURED VIDEOS SECTION */}
      <div id="videos">
        <FeaturedVideos settings={settings} />
      </div>

      {/* FEATURED PACKAGES SECTION */}
      <div id="packages">
        <FeaturedPackages 
          packages={featuredPackages} 
          loading={loading}
          onSelectPackage={(pkg) => {
            setSelectedPackage(pkg);
            setTimeout(() => {
              const contactSection = document.getElementById('contact');
              if (contactSection) {
                contactSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 100);
          }}
        />
      </div>

      {/* CONTACT FORM SECTION */}
      <div id="contact" className="py-20 md:py-32 px-0 md:px-12 bg-white font-sans">
        <div className="max-w-7xl mx-auto">
          <ContactForm prefilledPackage={selectedPackage} />
        </div>
      </div>

      {/* Full Screen Elegant Menu */}
      <div 
        className={`fixed inset-0 z-[100] transition-all duration-400 ease-in-out ${
          isMenuOpen ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 invisible'
        }`}
      >
        <div className="absolute inset-0 bg-white/40 backdrop-blur-xl" />
        
        <div className="relative h-full flex flex-col items-center justify-center p-6 text-center">
          <button 
            onClick={() => setIsMenuOpen(false)}
            className="absolute top-10 right-10 text-[10px] md:text-xs tracking-[0.4em] font-bold uppercase hover:opacity-50 transition-all duration-300 text-black"
          >
            {i18n.language === 'bs' ? 'Zatvori' : 'Close'}
          </button>

          <nav className="space-y-6 md:space-y-10 mb-12 text-black">
            <a 
              href="#home" 
              onClick={(e) => { e.preventDefault(); setIsMenuOpen(false); document.getElementById('home')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="group block"
            >
              <span className="text-3xl md:text-6xl font-serif tracking-wide transition-all duration-500 group-hover:italic">
                {i18n.language === 'bs' ? 'Naslovna' : 'Home'}
              </span>
            </a>
            <a 
              href="#portfolio" 
              onClick={(e) => { e.preventDefault(); setIsMenuOpen(false); document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="group block"
            >
              <span className="text-3xl md:text-6xl font-serif tracking-wide transition-all duration-500 group-hover:italic">
                Portfolio
              </span>
            </a>
            <a 
              href="#story" 
              onClick={(e) => { e.preventDefault(); setIsMenuOpen(false); document.getElementById('story')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="group block"
            >
              <span className="text-3xl md:text-6xl font-serif tracking-wide transition-all duration-500 group-hover:italic">
                Story
              </span>
            </a>
            <a 
              href="#gallery" 
              onClick={(e) => { e.preventDefault(); setIsMenuOpen(false); document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="group block"
            >
              <span className="text-3xl md:text-6xl font-serif tracking-wide transition-all duration-500 group-hover:italic">
                {i18n.language === 'bs' ? 'Galerija' : 'Gallery'}
              </span>
            </a>
            <a 
              href="#videos" 
              onClick={(e) => { e.preventDefault(); setIsMenuOpen(false); document.getElementById('videos')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="group block"
            >
              <span className="text-3xl md:text-6xl font-serif tracking-wide transition-all duration-500 group-hover:italic">
                {i18n.language === 'bs' ? 'Filmovi' : 'Films'}
              </span>
            </a>
            {featuredPackages && featuredPackages.length > 0 && (
              <a 
                href="#packages" 
                onClick={(e) => { e.preventDefault(); setIsMenuOpen(false); document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="group block"
              >
                <span className="text-3xl md:text-6xl font-serif tracking-wide transition-all duration-500 group-hover:italic">
                  {i18n.language === 'bs' ? 'Paketi' : 'Packages'}
                </span>
              </a>
            )}
            <a 
              href="#contact" 
              onClick={(e) => { e.preventDefault(); setIsMenuOpen(false); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="group block"
            >
              <span className="text-3xl md:text-6xl font-serif tracking-wide transition-all duration-500 group-hover:italic">
                {i18n.language === 'bs' ? 'Kontakt' : 'Contact'}
              </span>
            </a>
          </nav>

          {/* Language Selection */}
          <div className="flex items-center space-x-8 mb-12">
            <button 
              onClick={() => i18n.changeLanguage('bs')} 
              className={`text-[10px] md:text-xs tracking-[0.3em] transition-all duration-300 ${i18n.language === 'bs' ? 'font-bold border-b border-black' : 'font-light text-gray-400 hover:text-black'}`}
            >
              BS
            </button>
            <button 
              onClick={() => i18n.changeLanguage('en')} 
              className={`text-[10px] md:text-xs tracking-[0.3em] transition-all duration-300 ${i18n.language === 'en' ? 'font-bold border-b border-black' : 'font-light text-gray-400 hover:text-black'}`}
            >
              EN
            </button>
          </div>

          <div className="text-[10px] md:text-xs tracking-[0.2em] font-bold text-black space-y-2 uppercase">
            <div>{settings?.email || 'info@nejirazukic.com'}</div>
            <div>{settings?.phone || settings?.whatsapp || settings?.viber}</div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
    </>
  );
}

export default LandingPage;
